import React from "react";

// Renders a parsed jianpu song (from lib/jianpu parseJianpu) as real
// notation: numbers with octave dots above/below, underlines for eighths and
// sixteenths, dashes for held beats, bar lines.
//
// Props:
//   song        — parsed song object (required)
//   activeIndex — note index currently sounding (playback highlight)
//   targetIndex — note index to play next (wait-for-me pulse)
//   statuses    — { [noteIndex]: "hit" | "miss" } from play-along scoring
//   showHeader  — render title/key/time/tempo line (default true)

const Dots = ({ count, below }) => (
  <span className={`jp-cell-dots${below ? " jp-cell-dots--below" : ""}`}>
    {Array.from({ length: count }, (_, i) => (
      <span key={i} className="jp-dot" />
    ))}
  </span>
);

const NoteCell = ({ note, state }) => {
  const { render } = note;
  const cls = ["jp-cell"];
  if (state) cls.push(`jp-cell--${state}`);
  return (
    <span className={cls.join(" ")}>
      <Dots count={note.octave > 0 ? note.octave : 0} />
      <span className="jp-cell-num">
        {note.accidental === 1 && <span className="jp-acc">♯</span>}
        {note.accidental === -1 && <span className="jp-acc">♭</span>}
        {note.degree}
        {render.dotted && <span className="jp-dotted">·</span>}
      </span>
      {render.underlines > 0 ? (
        <span className="jp-u">
          {Array.from({ length: render.underlines }, (_, i) => (
            <span key={i} />
          ))}
        </span>
      ) : (
        <span className="jp-u" aria-hidden />
      )}
      <Dots count={note.octave < 0 ? -note.octave : 0} below />
    </span>
  );
};

const DashCell = ({ state }) => (
  <span className={`jp-cell${state ? ` jp-cell--${state}` : ""}`}>
    <span className="jp-cell-dots" />
    <span className="jp-cell-num">–</span>
    <span className="jp-u" aria-hidden />
    <span className="jp-cell-dots jp-cell-dots--below" />
  </span>
);

export default function JianpuSheet({
  song,
  activeIndex = -1,
  targetIndex = -1,
  statuses = {},
  showHeader = true,
}) {
  if (!song) return null;

  // Group notes by bar; each note renders as its cell plus dash cells.
  const bars = [];
  song.notes.forEach((note, index) => {
    if (!bars[note.bar]) bars[note.bar] = [];
    bars[note.bar].push({ note, index });
  });

  const stateFor = (index) => {
    if (index === targetIndex) return "target";
    if (statuses[index]) return statuses[index];
    if (index === activeIndex) return "active";
    return null;
  };

  return (
    <div className="jp-sheet">
      {showHeader && (
        <>
          <h2 className="jp-sheet-title">{song.title || "Untitled"}</h2>
          <div className="jp-sheet-meta">
            <span>1={song.key}</span>
            <span>
              {song.time.num}/{song.time.den}
            </span>
            <span>♩ = {song.tempo}</span>
          </div>
        </>
      )}
      <div className="jp-staff">
        {bars.map((cells, barIdx) => (
          <span className="jp-bar" key={barIdx}>
            {cells.map(({ note, index }) => (
              <React.Fragment key={index}>
                <NoteCell note={note} state={stateFor(index)} />
                {Array.from({ length: note.render.dashes }, (_, i) => (
                  <DashCell key={i} state={stateFor(index)} />
                ))}
              </React.Fragment>
            ))}
            <span className="jp-barline">{barIdx === bars.length - 1 ? "‖" : "|"}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
